import {onClient} from '@salesforce/commerce-sdk-react/utils'
import {
    hasAllKeys,
    omitNullableParameters,
    mergeOptions,
    pickValidParams
} from '@salesforce/commerce-sdk-react/hooks/utils'
import {useAuthorizationHeader} from '@salesforce/commerce-sdk-react/hooks/useAuthorizationHeader'
import {useInfiniteQuery as useReactInfinityQuery} from '@tanstack/react-query'
import useCommerceApi from '@salesforce/commerce-sdk-react/hooks/useCommerceApi'
import * as queryKeyHelpers from '@salesforce/commerce-sdk-react/hooks/ShopperSearch/queryKeyHelpers'
import {ShopperSearch} from 'commerce-sdk-isomorphic'

// Based on https://github.com/SalesforceCommerceCloud/pwa-kit/blob/release-3.6.x/packages/commerce-sdk-react/src/hooks/useQuery.ts
export const useInfinityQuery = (apiOptions, queryOptions, hookConfig) => {
    const authenticatedMethod = useAuthorizationHeader(hookConfig.method)
    const wrappedMethod = async ({pageParam}) => {
        apiOptions.parameters.offset = pageParam || 0

        return await authenticatedMethod(apiOptions)
    }
    return useReactInfinityQuery({
        queryKey: hookConfig.queryKey,
        queryFn: wrappedMethod,
        enabled:
            hookConfig.enabled !== false &&
            hasAllKeys(apiOptions.parameters, hookConfig.requiredParameters),

        ...queryOptions,
        ...(queryOptions?.retry ? {retry: onClient() ? queryOptions?.retry : false} : {}),
        ...(queryOptions?.retryOnMount
            ? {retryOnMount: onClient() ? queryOptions?.retryOnMount : false}
            : {}),

        getNextPageParam: (lastPage) => {
            if (lastPage.offset + lastPage.limit < lastPage.total) {
                return lastPage.offset + lastPage.limit
            }
        }
    })
}

//Based on https://github.com/SalesforceCommerceCloud/pwa-kit/blob/release-3.6.x/packages/commerce-sdk-react/src/hooks/ShopperSearch/query.ts
export const useProductInfinitySearch = (apiOptions, queryOptions = {}) => {
    const {shopperSearch: client} = useCommerceApi()
    const methodName = 'productSearch'
    const requiredParameters = ShopperSearch.paramKeys[`${methodName}Required`]

    const netOptions = omitNullableParameters(mergeOptions(client, apiOptions))
    const parameters = pickValidParams(netOptions.parameters, ShopperSearch.paramKeys[methodName])
    const queryKey = ['infinity', ...queryKeyHelpers[methodName].queryKey(netOptions.parameters)]
    const method = async (options) => await client[methodName](options)

    return useInfinityQuery(
        {...netOptions, parameters},
        {...queryOptions, meta: {displayName: 'useProductInfinitySearch', ...queryOptions.meta}},
        {
            method,
            queryKey,
            requiredParameters
        }
    )
}
